//#region FACTORY - GLOBAL PATHS

/**
 * Factory for generating Firestore global paths in a chainable way.
 *
 * Root hierarchy: `global/...`
 *
 * ---
 * ### Usage
 * ```ts
 * const globalPaths = createGlobalPaths();
 *
 * globalPaths.global();                   // → "global"
 * globalPaths.version('version_prod');    // → "global/version_prod"
 * globalPaths.stockTags();                // → "global/global_stock_tags"
 * globalPaths.settings();                 // → "global/settings"
 * globalPaths.groupAccess();              // → "global/settings/group_access"
 * globalPaths.sharedSettings();           // → "global/settings/shared_settings"
 * globalPaths.priceBands();               // → "global/settings/shared_settings/price_bands"
 * globalPaths.defaultShelftalkerLabels(); // → "global/settings/shared_settings/default_shelftalker_labels"
 * ```
 */
export const createGlobalPaths = (): {
  base: string;
  customPath(...segments: string[]): string;
  defaultShelftalkerLabels(): string;
  global(): string;
  groupAccess(): string;
  priceBands(): string;
  settings(): string;
  sharedSettings(): string;
  stockTags(): string;
  version(documentId: string): string;
} => {
  return {
    /** Base path for all global data */
    base: 'global',

    /** Root segment for global data: `global` */
    global(): string {
      return this.base;
    },

    /** Build any custom path under global */
    customPath(...segments: string[]): string {
      return `${this.global()}/${segments.join('/')}`;
    },

    /** `/global/{documentId}` */
    version(documentId: string): string {
      return `${this.global()}/${documentId}`;
    },

    /** `/global/global_stock_tags` */
    stockTags(): string {
      return `${this.global()}/global_stock_tags`;
    },

    // ────────────────────────────────
    // Settings paths
    // ────────────────────────────────

    /** `/global/settings` */
    settings(): string {
      return `${this.global()}/settings`;
    },

    /** `/global/settings/group_access` */
    groupAccess(): string {
      return `${this.settings()}/group_access`;
    },

    /** `/global/settings/shared_settings` */
    sharedSettings(): string {
      return `${this.settings()}/shared_settings`;
    },

    /** `/global/settings/shared_settings/default_shelftalker_labels` */
    defaultShelftalkerLabels(): string {
      return `${this.sharedSettings()}/default_shelftalker_labels`;
    },

    /** `/global/settings/shared_settings/price_bands` */
    priceBands(): string {
      return `${this.sharedSettings()}/price_bands`;
    },
  };
};

//#endregion
